import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { Card, Spinner, Alert, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faCompress, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { API_URL } from "../../utils/constant";
import moment from 'moment';

const CampaignGraph = ({ campaignId, container }) => {
    const mountRef = useRef(null);
    const labelsRef = useRef(null);
    const sceneRef = useRef(null);
    const rendererRef = useRef(null);
    const cameraRef = useRef(null);
    const controlsRef = useRef(null);
    const animationFrameRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [graphData, setGraphData] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [hoveredNode, setHoveredNode] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

    // Entity type colors
    const entityColors = {
        'Report': 0x0d6efd,
        'Malware': 0xdc3545,
        'Indicator': 0xffc107,
        'Threat-Actor': 0x6f42c1,
        'Campaign': 0x20c997,
        'Intrusion-Set': 0xfd7e14,
        'Attack-Pattern': 0x17a2b8,
        'StixFile': 0x28a745,
        'Vulnerability': 0xe83e8c,
        'default': 0x6c757d
    };

    // Entity type sizes
    const entitySizes = {
        'Report': 0.8,
        'Malware': 1.0,
        'Indicator': 0.7,
        'Threat-Actor': 1.2,
        'Campaign': 1.0,
        'Intrusion-Set': 1.1,
        'Attack-Pattern': 0.9,
        'StixFile': 0.8,
        'Vulnerability': 0.9,
        'default': 0.8
    };

    // Relationship type colors for edges
    const relationshipColors = {
        'indicates': 0x00ff00,
        'uses': 0xff6b00,
        'targets': 0xff0000,
        'based-on': 0x00ffff,
        'related-to': 0xffff00,
        'default': 0x888888
    };

    const fetchGraphData = async (cursorParam = null) => {
        try {
            let url = `${API_URL}/threat-intel/campaigns/${campaignId}/graph?count=50`;
            if (cursorParam) {
                url += `&cursor=${encodeURIComponent(cursorParam)}`;
            }

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('session')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.error) {
                throw new Error(data.userMessage || 'Failed to fetch graph data');
            }

            const newData = data.data;
            if (cursorParam && graphData) {
                // Append to existing data
                setGraphData(prev => ({
                    ...prev,
                    objects: [...prev.objects, ...newData.objects],
                    pagination: newData.pagination
                }));
            } else {
                setGraphData(newData);
            }

            setCursor(newData.pagination?.endCursor);
            setHasMore(newData.pagination?.hasNextPage || false);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (campaignId) {
            setLoading(true);
            fetchGraphData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId]);

    const loadMore = () => {
        if (cursor && hasMore) {
            setLoading(true);
            fetchGraphData(cursor);
        }
    };

    useEffect(() => {
        if (!mountRef.current || !graphData || graphData.objects.length === 0) return;

        // Scene setup
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x1a1a1a);
        sceneRef.current = scene;

        // Camera setup
        const camera = new THREE.PerspectiveCamera(
            75,
            mountRef.current.clientWidth / mountRef.current.clientHeight,
            0.1,
            1000
        );
        camera.position.set(0, 0, 15);
        cameraRef.current = camera;

        // Renderer setup
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
        renderer.setPixelRatio(window.devicePixelRatio);
        mountRef.current.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // Lighting
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        scene.add(ambientLight);

        const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight1.position.set(10, 10, 10);
        scene.add(directionalLight1);

        const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
        directionalLight2.position.set(-10, -10, -10);
        scene.add(directionalLight2);

        // Create nodes and edges
        const nodes = [];
        const nodeMap = new Map();
        const edges = [];
        const labelElements = [];

        // Add container (campaign) as center node
        if (container) {
            const containerGeometry = new THREE.SphereGeometry(1.2, 32, 32);
            const containerMaterial = new THREE.MeshPhongMaterial({ 
                color: 0x0d6efd,
                emissive: 0x0d6efd,
                emissiveIntensity: 0.3
            });
            const containerNode = new THREE.Mesh(containerGeometry, containerMaterial);
            containerNode.position.set(0, 0, 0);
            containerNode.userData = { 
                id: container.id, 
                name: container.name, 
                type: 'Report',
                isContainer: true 
            };
            scene.add(containerNode);
            nodes.push(containerNode);
            nodeMap.set(container.id, containerNode);
        }

        // Separate entities from relationships
        const relationshipTypes = ['indicates', 'uses', 'targets', 'based-on', 'related-to'];
        const entities = [];
        const relationships = [];
        
        graphData.objects.forEach((obj) => {
            if (relationshipTypes.includes(obj.entity_type?.toLowerCase())) {
                relationships.push(obj);
            } else {
                entities.push(obj);
            }
        });

        // Create nodes for entity objects only (not relationships)
        entities.forEach((obj, index) => {
            const entityType = obj.entity_type || 'default';
            const color = entityColors[entityType] || entityColors.default;
            const size = entitySizes[entityType] || entitySizes.default;

            const geometry = new THREE.SphereGeometry(size, 32, 32);
            const material = new THREE.MeshPhongMaterial({ 
                color: color,
                emissive: color,
                emissiveIntensity: 0.2
            });
            const node = new THREE.Mesh(geometry, material);

            // Position nodes in a sphere around the center with better distribution
            const radius = 7 + (index % 4) * 1.5;
            const theta = (index * 137.508) % 360; // Golden angle distribution for even spacing
            const phi = Math.acos(1 - 2 * (index / Math.max(entities.length, 1)));
            const x = radius * Math.sin(phi) * Math.cos(theta);
            const y = radius * Math.sin(phi) * Math.sin(theta);
            const z = radius * Math.cos(phi);

            node.position.set(x, y, z);
            
            // Get display name based on entity type
            let displayName = obj.name || 'Unnamed';
            if (entityType === 'StixFile' && obj.hashes && obj.hashes.length > 0) {
                displayName = `${obj.hashes[0].algorithm}: ${obj.hashes[0].hash.substring(0, 16)}...`;
            } else if (entityType === 'Attack-Pattern' && obj.x_mitre_id) {
                displayName = obj.x_mitre_id;
            } else if (entityType === 'Indicator' && obj.pattern) {
                // Extract hash from pattern if available
                const hashMatch = obj.pattern.match(/SHA-256'\s*=\s*'([^']+)/);
                if (hashMatch) {
                    displayName = `SHA-256: ${hashMatch[1].substring(0, 16)}...`;
                }
            }
            
            node.userData = { 
                id: obj.id, 
                name: displayName, 
                type: entityType,
                data: obj,
                index: index
            };
            scene.add(node);
            nodes.push(node);
            nodeMap.set(obj.id, node);

            // Create edge from container to object
            if (container) {
                const edgeGeometry = new THREE.BufferGeometry().setFromPoints([
                    new THREE.Vector3(0, 0, 0),
                    new THREE.Vector3(x, y, z)
                ]);
                const edgeMaterial = new THREE.LineBasicMaterial({ 
                    color: 0x666666, 
                    opacity: 0.6,
                    transparent: true,
                    linewidth: 2
                });
                const edge = new THREE.Line(edgeGeometry, edgeMaterial);
                scene.add(edge);
                edges.push({ line: edge, from: container.id, to: obj.id, type: 'contains' });
            }

            // Create label element
            const labelDiv = document.createElement('div');
            labelDiv.className = 'node-label';
            labelDiv.style.cssText = `
                position: absolute;
                color: white;
                font-size: 10px;
                font-weight: 600;
                background: rgba(0, 0, 0, 0.85);
                padding: 3px 6px;
                border-radius: 3px;
                pointer-events: none;
                white-space: nowrap;
                border: 1.5px solid ${'#' + color.toString(16).padStart(6, '0')};
                max-width: 200px;
                overflow: hidden;
                text-overflow: ellipsis;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            `;
            const labelText = displayName.length > 30 ? displayName.substring(0, 30) + '...' : displayName;
            labelDiv.textContent = `${entityType}: ${labelText}`;
            if (labelsRef.current) {
                labelsRef.current.appendChild(labelDiv);
            }
            labelElements.push({ element: labelDiv, node: node });
        });

        // Create edges from relationships
        relationships.forEach((rel) => {
            const fromNode = nodeMap.get(rel.from?.id);
            const toNode = nodeMap.get(rel.to?.id);
            
            if (fromNode && toNode) {
                const relType = rel.relationship_type || rel.entity_type || 'default';
                const relColor = relationshipColors[relType] || relationshipColors.default;
                
                const edgeGeometry = new THREE.BufferGeometry().setFromPoints([
                    fromNode.position.clone(),
                    toNode.position.clone()
                ]);
                const edgeMaterial = new THREE.LineBasicMaterial({ 
                    color: relColor, 
                    opacity: 0.7,
                    transparent: true,
                    linewidth: 2
                });
                const edge = new THREE.Line(edgeGeometry, edgeMaterial);
                scene.add(edge);
                edges.push({ 
                    line: edge, 
                    from: rel.from.id, 
                    to: rel.to.id, 
                    type: relType,
                    relationship: rel
                });
            }
        });

        // Add label for container
        if (container && labelsRef.current) {
            const containerLabelDiv = document.createElement('div');
            containerLabelDiv.className = 'node-label';
            containerLabelDiv.style.cssText = `
                position: absolute;
                color: white;
                font-size: 11px;
                font-weight: 700;
                background: rgba(13, 110, 253, 0.9);
                padding: 5px 8px;
                border-radius: 4px;
                pointer-events: none;
                white-space: nowrap;
                border: 2px solid #0d6efd;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                box-shadow: 0 2px 6px rgba(13, 110, 253, 0.4);
            `;
            const containerDisplayName = container.name && container.name.length > 30 ? container.name.substring(0, 30) + '...' : (container.name || 'Unnamed');
            containerLabelDiv.textContent = `Campaign: ${containerDisplayName}`;
            labelsRef.current.appendChild(containerLabelDiv);
            const containerNode = nodeMap.get(container.id);
            if (containerNode) {
                labelElements.push({ element: containerLabelDiv, node: containerNode });
            }
        }

        // Mouse controls
        let mouseDown = false;
        let mouseX = 0;
        let mouseY = 0;
        let targetRotationX = 0;
        let targetRotationY = 0;
        let currentRotationX = 0;
        let currentRotationY = 0;

        const onMouseDown = (event) => {
            mouseDown = true;
            mouseX = event.clientX;
            mouseY = event.clientY;
        };

        const onMouseMove = (event) => {
            if (!mouseDown) return;
            const deltaX = event.clientX - mouseX;
            const deltaY = event.clientY - mouseY;
            targetRotationY += deltaX * 0.01;
            targetRotationX += deltaY * 0.01;
            mouseX = event.clientX;
            mouseY = event.clientY;
        };

        const onMouseUp = () => {
            mouseDown = false;
        };

        // Zoom controls - click to zoom in/out
        let zoomLevel = 15;
        const handleZoomIn = () => {
            zoomLevel = Math.max(5, zoomLevel - 2);
            camera.position.z = zoomLevel;
        };
        const handleZoomOut = () => {
            zoomLevel = Math.min(50, zoomLevel + 2);
            camera.position.z = zoomLevel;
        };

        renderer.domElement.addEventListener('mousedown', onMouseDown);
        renderer.domElement.addEventListener('mousemove', onMouseMove);
        renderer.domElement.addEventListener('mouseup', onMouseUp);

        // Raycasting for hover
        const raycaster = new THREE.Raycaster();
        const mouse = new THREE.Vector2();
        let currentHoveredNode = null;

        const onMouseMoveHover = (event) => {
            const rect = renderer.domElement.getBoundingClientRect();
            mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

            raycaster.setFromCamera(mouse, camera);
            const intersects = raycaster.intersectObjects(nodes);

            if (intersects.length > 0) {
                const node = intersects[0].object;
                if (currentHoveredNode !== node) {
                    if (currentHoveredNode) {
                        currentHoveredNode.material.emissiveIntensity = 0.2;
                        currentHoveredNode.scale.set(1, 1, 1);
                    }
                    currentHoveredNode = node;
                    node.material.emissiveIntensity = 0.8;
                    node.scale.set(1.3, 1.3, 1.3);
                    renderer.domElement.style.cursor = 'pointer';
                    
                    // Update tooltip
                    setHoveredNode(node.userData);
                    setTooltipPosition({ x: event.clientX, y: event.clientY });
                } else {
                    // Update tooltip position if same node
                    setTooltipPosition({ x: event.clientX, y: event.clientY });
                }
            } else {
                if (currentHoveredNode) {
                    currentHoveredNode.material.emissiveIntensity = 0.2;
                    currentHoveredNode.scale.set(1, 1, 1);
                    currentHoveredNode = null;
                }
                renderer.domElement.style.cursor = 'default';
                setHoveredNode(null);
            }
        };

        renderer.domElement.addEventListener('mousemove', onMouseMoveHover);

        // Function to update label positions
        const updateLabelPositions = () => {
            labelElements.forEach(({ element, node }) => {
                const vector = node.position.clone();
                vector.project(camera);
                
                const x = (vector.x * 0.5 + 0.5) * mountRef.current.clientWidth;
                const y = (-vector.y * 0.5 + 0.5) * mountRef.current.clientHeight;
                
                // Only show label if node is in front of camera
                if (vector.z < 1) {
                    element.style.display = 'block';
                    element.style.left = x + 'px';
                    element.style.top = y + 'px';
                    element.style.transform = 'translate(-50%, -100%)';
                } else {
                    element.style.display = 'none';
                }
            });
        };

        // Store initial positions for nodes
        const initialPositions = new Map();
        nodes.forEach((node, index) => {
            if (!node.userData.isContainer) {
                const radius = 7 + (index % 4) * 1.5;
                const theta = (index * 137.508) % 360;
                const phi = Math.acos(1 - 2 * (index / Math.max(entities.length, 1)));
                initialPositions.set(node, {
                    radius: radius,
                    theta: theta,
                    phi: phi
                });
            }
        });

        // Animation loop - no automatic rotation, only smooth user interactions
        const animate = () => {
            animationFrameRef.current = requestAnimationFrame(animate);

            // Smooth rotation interpolation (always smooth, not just when dragging)
            currentRotationX += (targetRotationX - currentRotationX) * 0.15;
            currentRotationY += (targetRotationY - currentRotationY) * 0.15;

            // Update node positions based on rotation (no automatic movement)
            nodes.forEach((node, index) => {
                if (!node.userData.isContainer) {
                    const initial = initialPositions.get(node);
                    if (initial) {
                        const phi = initial.phi + currentRotationX;
                        const theta = initial.theta + currentRotationY;
                        const x = initial.radius * Math.sin(phi) * Math.cos(theta);
                        const y = initial.radius * Math.sin(phi) * Math.sin(theta);
                        const z = initial.radius * Math.cos(phi);

                        node.position.lerp(new THREE.Vector3(x, y, z), 0.3);
                    }
                }
            });

            // Update all edges
            edges.forEach((edgeObj) => {
                if (edgeObj.type === 'contains' && container) {
                    // Container to entity edges
                    const toNode = nodeMap.get(edgeObj.to);
                    if (toNode) {
                        edgeObj.line.geometry.setFromPoints([
                            new THREE.Vector3(0, 0, 0),
                            toNode.position
                        ]);
                        edgeObj.line.geometry.attributes.position.needsUpdate = true;
                    }
                } else {
                    // Relationship edges
                    const fromNode = nodeMap.get(edgeObj.from);
                    const toNode = nodeMap.get(edgeObj.to);
                    if (fromNode && toNode) {
                        edgeObj.line.geometry.setFromPoints([
                            fromNode.position.clone(),
                            toNode.position.clone()
                        ]);
                        edgeObj.line.geometry.attributes.position.needsUpdate = true;
                    }
                }
            });

            // Update label positions
            updateLabelPositions();

            renderer.render(scene, camera);
        };

        animate();

        // Handle resize
        const handleResize = () => {
            if (!mountRef.current) return;
            const width = mountRef.current.clientWidth;
            const height = mountRef.current.clientHeight;
            camera.aspect = width / height;
            camera.updateProjectionMatrix();
            renderer.setSize(width, height);
        };

        window.addEventListener('resize', handleResize);
        
        // Also listen for fullscreen changes to update size
        const handleFullscreenResize = () => {
            setTimeout(handleResize, 100);
        };
        
        document.addEventListener('fullscreenchange', handleFullscreenResize);
        document.addEventListener('webkitfullscreenchange', handleFullscreenResize);
        document.addEventListener('msfullscreenchange', handleFullscreenResize);

        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('fullscreenchange', handleFullscreenResize);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenResize);
            document.removeEventListener('msfullscreenchange', handleFullscreenResize);
            renderer.domElement.removeEventListener('mousedown', onMouseDown);
            renderer.domElement.removeEventListener('mousemove', onMouseMove);
            renderer.domElement.removeEventListener('mouseup', onMouseUp);
            renderer.domElement.removeEventListener('mousemove', onMouseMoveHover);

            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }

            // Remove label elements
            if (labelsRef.current) {
                labelElements.forEach(({ element }) => {
                    if (element.parentNode === labelsRef.current) {
                        labelsRef.current.removeChild(element);
                    }
                });
            }

            // Dispose of geometries and materials
            scene.traverse((object) => {
                if (object.geometry) object.geometry.dispose();
                if (object.material) {
                    if (Array.isArray(object.material)) {
                        object.material.forEach(material => material.dispose());
                    } else {
                        object.material.dispose();
                    }
                }
            });

            // Dispose edge geometries and materials
            edges.forEach((edgeObj) => {
                if (edgeObj.line) {
                    if (edgeObj.line.geometry) edgeObj.line.geometry.dispose();
                    if (edgeObj.line.material) edgeObj.line.material.dispose();
                }
            });

            if (renderer.domElement && mountRef.current) {
                mountRef.current.removeChild(renderer.domElement);
            }
            renderer.dispose();
        };
    }, [graphData, container, campaignId]);

    const toggleFullscreen = () => {
        const containerElement = document.getElementById('campaign-graph-container');
        if (!containerElement) return;
        
        if (!isFullscreen) {
            if (containerElement.requestFullscreen) {
                containerElement.requestFullscreen();
            } else if (containerElement.webkitRequestFullscreen) {
                containerElement.webkitRequestFullscreen();
            } else if (containerElement.msRequestFullscreen) {
                containerElement.msRequestFullscreen();
            } else if (containerElement.mozRequestFullScreen) {
                containerElement.mozRequestFullScreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            }
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, []);

    if (loading && !graphData) {
        return (
            <div className="text-center py-5" style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div>
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Loading graph data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger">
                <Alert.Heading>Error</Alert.Heading>
                <p>{error}</p>
            </Alert>
        );
    }

    if (!graphData || graphData.objects.length === 0) {
        return (
            <div className="text-center py-5">
                <p className="text-muted">No graph data available for this campaign.</p>
            </div>
        );
    }

    return (
        <div 
            id="campaign-graph-container"
            style={{ 
                position: 'relative', 
                width: '100%', 
                height: '800px',
                minHeight: '800px'
            }}
        >
            <div
                ref={mountRef}
                style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: '#1a1a1a',
                    position: 'relative'
                }}
            />
            <div
                ref={labelsRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 10,
                    overflow: 'visible'
                }}
            />
            {hoveredNode && (
                <div
                    style={{
                        position: 'fixed',
                        left: `${Math.min(tooltipPosition.x + 15, window.innerWidth - 320)}px`,
                        top: `${Math.max(10, tooltipPosition.y - 10)}px`,
                        background: 'rgba(0, 0, 0, 0.95)',
                        color: 'white',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        zIndex: 10000,
                        maxWidth: '300px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                        border: `2px solid ${'#' + (entityColors[hoveredNode.type] || entityColors.default).toString(16).padStart(6, '0')}`,
                        pointerEvents: 'none'
                    }}
                >
                    <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                        {hoveredNode.type}: {hoveredNode.name || 'Unnamed'}
                    </div>
                    {hoveredNode.data && (
                        <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                            {hoveredNode.data.description && (
                                <div style={{ marginBottom: '8px', opacity: 0.9 }}>
                                    {hoveredNode.data.description.length > 150 
                                        ? hoveredNode.data.description.substring(0, 150) + '...'
                                        : hoveredNode.data.description}
                                </div>
                            )}
                            {hoveredNode.data.created_at && (
                                <div style={{ marginBottom: '4px', opacity: 0.8 }}>
                                    <strong>Created:</strong> {moment(hoveredNode.data.created_at).format('MMM DD, YYYY')}
                                </div>
                            )}
                            {hoveredNode.data.objectLabel && hoveredNode.data.objectLabel.length > 0 && (
                                <div style={{ marginTop: '8px' }}>
                                    <strong>Labels:</strong>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                        {hoveredNode.data.objectLabel.slice(0, 3).map((label, idx) => (
                                            <span
                                                key={idx}
                                                style={{
                                                    background: label.color || '#6c757d',
                                                    padding: '2px 6px',
                                                    borderRadius: '3px',
                                                    fontSize: '10px'
                                                }}
                                            >
                                                {label.value}
                                            </span>
                                        ))}
                                        {hoveredNode.data.objectLabel.length > 3 && (
                                            <span style={{ fontSize: '10px', opacity: 0.8 }}>
                                                +{hoveredNode.data.objectLabel.length - 3} more
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
            <div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                        variant="light"
                        size="sm"
                        onClick={() => {
                            const currentZ = cameraRef.current?.position.z || 15;
                            const newZ = Math.max(5, currentZ - 2);
                            if (cameraRef.current) cameraRef.current.position.z = newZ;
                        }}
                        style={{ borderRadius: '6px', minWidth: '40px', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
                        title="Zoom In"
                    >
                        <strong>+</strong>
                    </Button>
                    <Button
                        variant="light"
                        size="sm"
                        onClick={() => {
                            const currentZ = cameraRef.current?.position.z || 15;
                            const newZ = Math.min(50, currentZ + 2);
                            if (cameraRef.current) cameraRef.current.position.z = newZ;
                        }}
                        style={{ borderRadius: '6px', minWidth: '40px', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
                        title="Zoom Out"
                    >
                        <strong>−</strong>
                    </Button>
                </div>
                <Button
                    variant="light"
                    size="sm"
                    onClick={toggleFullscreen}
                    style={{ borderRadius: '6px', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
                    title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                >
                    <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
                </Button>
                {hasMore && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={loadMore}
                        disabled={loading}
                        style={{ borderRadius: '6px', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
                    >
                        {loading ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faRefresh} className="me-1" />
                                Load More
                            </>
                        )}
                    </Button>
                )}
            </div>
            <div style={{ position: 'absolute', bottom: '10px', left: '10px', zIndex: 10, background: 'rgba(0,0,0,0.85)', padding: '12px 16px', borderRadius: '8px', color: 'white', fontSize: '0.85rem', maxWidth: '320px', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '0.9rem' }}>Graph Information</div>
                <div style={{ marginBottom: '6px', fontSize: '0.8rem' }}>
                    <strong style={{ color: 'white' }}>Nodes:</strong> {graphData.objects.filter(obj => !['indicates', 'uses', 'targets', 'based-on', 'related-to'].includes(obj.entity_type?.toLowerCase())).length + (container ? 1 : 0)} entities
                </div>
                <div style={{ color: 'white', marginBottom: '6px' }}><strong>Entity Types:</strong></div>
                <div style={{ fontSize: '0.75rem', lineHeight: '1.6', marginBottom: '8px' }}>
                    <div>🔵 <span style={{ color: '#0d6efd' }}>Blue</span> = Report</div>
                    <div>🔴 <span style={{ color: '#dc3545' }}>Red</span> = Malware</div>
                    <div>🟡 <span style={{ color: '#ffc107' }}>Yellow</span> = Indicator</div>
                    <div>🟦 <span style={{ color: '#17a2b8' }}>Cyan</span> = Attack-Pattern</div>
                    <div>🟢 <span style={{ color: '#28a745' }}>Green</span> = StixFile</div>
                    <div>🟣 <span style={{ color: '#6f42c1' }}>Purple</span> = Threat-Actor</div>
                    <div>🟠 <span style={{ color: '#fd7e14' }}>Orange</span> = Intrusion-Set</div>
                </div>
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                    <div style={{ marginBottom: '4px', fontSize: '0.75rem' }}><strong>Relationships:</strong></div>
                    <div style={{ fontSize: '0.7rem', lineHeight: '1.5' }}>
                        <div><span style={{ color: '#00ff00', fontWeight: 'bold' }}>━━</span> <span style={{ color: '#00ff00' }}>Green</span> = indicates</div>
                        <div><span style={{ color: '#ff6b00', fontWeight: 'bold' }}>━━</span> <span style={{ color: '#ff6b00' }}>Orange</span> = uses</div>
                        <div><span style={{ color: '#ff0000', fontWeight: 'bold' }}>━━</span> <span style={{ color: '#ff0000' }}>Red</span> = targets</div>
                        <div><span style={{ color: '#888888', fontWeight: 'bold' }}>━━</span> <span style={{ color: '#888888' }}>Gray</span> = contains</div>
                    </div>
                </div>
                <div style={{ fontSize: '0.7rem', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.2)', opacity: 0.9 }}>
                    💡 Hover over nodes for details • Click and drag to rotate • Use +/- buttons to zoom
                </div>
            </div>
        </div>
    );
};

export default CampaignGraph;
